import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { MODAL_ROUTES } from '@woolworthsnz/shop';
import {
	SlottedModalComponent,
	RoutedModalBaseComponent,
	HistoryService,
	ButtonState,
	AppSettingsService,
} from '@woolworthsnz/styleguide';
import { FulfilmentFlexibleDeliverySlotMultipleSelectionComponent } from '@woolworthsnz/fulfilment';

import { FormGroup, ReactiveFormsModule, FormBuilder, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FulfilmentState, FulfilmentStoreService, FulfilmentService } from '@woolworthsnz/fulfilment';
import { ButtonComponent } from '@woolworthsnz/styleguide';
import equal from 'fast-deep-equal';
import { NgIf, CurrencyPipe } from '@angular/common';
import {
	ExpressFulfilmentSettingsResponse,
	FulfilmentWindowSummarySlot,
	OrderViewModel,
} from '@woolworthsnz/trader-api';

import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';

@Component({
	selector: 'wnz-change-flexible-slot-modal',
	templateUrl: './change-flexible-slot-modal.component.html',
	styleUrls: ['./change-fexible-slot-modal.component.scss'],
	standalone: true,
	imports: [
		NgIf,
		SlottedModalComponent,
		FulfilmentFlexibleDeliverySlotMultipleSelectionComponent,
		ReactiveFormsModule,
		ButtonComponent,
		CurrencyPipe,
	],
})
export class ChangeFlexibleSlotModalComponent extends RoutedModalBaseComponent implements OnInit, OnDestroy {
	modalRoutes = MODAL_ROUTES;
	controlName = 'slot'; // key in parent FormGroup's `value` to save user selected time slot, example: 'slot'.

	fb: FormBuilder;
	changeFlexibleSlotModalForm: FormGroup;
	slot?: FulfilmentWindowSummarySlot;
	slots?: FulfilmentWindowSummarySlot[];
	isOkEnabled: boolean;

	continueButtonState = ButtonState.default;
	initalSelectedSlot?: FulfilmentWindowSummarySlot;
	maxItemQuantityTotalAllowedForFlexibleDelivery: number | undefined;
	flexibleDeliveryFee: number | undefined;
	private destroyed$: Subject<boolean> = new Subject();
	expressFulfilmentSettings: ExpressFulfilmentSettingsResponse;

	constructor(
		protected router: Router,
		protected historyService: HistoryService,
		protected activatedRoute: ActivatedRoute,
		private fulfilmentStoreService: FulfilmentStoreService,
		private fulfilmentService: FulfilmentService,
		private appSettingsService: AppSettingsService,
		private cdr: ChangeDetectorRef
	) {
		super(router, activatedRoute, historyService);
	}

	fulfilmentMethod?: OrderViewModel.DeliveryMethodEnum;
	fulfilmentStoreServiceState$ = this.fulfilmentStoreService.state$.pipe(
		takeUntilDestroyed(),
		distinctUntilChanged((a: FulfilmentState, b: FulfilmentState) => equal(a, b))
	);
	private _allSlots: FulfilmentWindowSummarySlot[];
	private _selectedSlot?: FulfilmentWindowSummarySlot;

	set selectedSlot(s: FulfilmentWindowSummarySlot | undefined) {
		this._selectedSlot = s;
	}
	// whenever the slots get set always update the selected slot
	set allSlots(fulfillmentWindowSummarySlots: FulfilmentWindowSummarySlot[]) {
		this._allSlots = fulfillmentWindowSummarySlots;
		this.selectedSlot = this.allSlots?.find(
			(slot) => slot.status === FulfilmentWindowSummarySlot.StatusEnum.Selected && slot.isFlexibleDelivery
		);

		this.initalSelectedSlot = this._selectedSlot ?? this.fulfilmentService.getFlexibleSelectedSlot();
		this.isOkEnabled = this.initalSelectedSlot != null;

		this.updateForm();
	}

	updateForm(): void {
		if (this.initalSelectedSlot) {
			this.changeFlexibleSlotModalForm.patchValue({
				slot: `${this.initalSelectedSlot.id}`,
			});
		} else {
			this.changeFlexibleSlotModalForm.patchValue({
				slot: null,
			});
		}

		this.cdr.markForCheck();
	}

	get allSlots(): FulfilmentWindowSummarySlot[] {
		return this._allSlots;
	}

	get flexibleDeliverySlots(): FulfilmentWindowSummarySlot[] {
		return this.allSlots?.filter((slot) => slot.isFlexibleDelivery);
	}

	ngOnInit(): void {
		this.changeFlexibleSlotModalForm = new FormGroup({
			slot: new FormControl(''),
			day: new FormControl(''),
			method: new FormControl(''),
		});

		this.fulfilmentStoreServiceState$.subscribe((fulfilmentState: FulfilmentState) => {
			this.fulfilmentMethod = fulfilmentState.method;
			if (!fulfilmentState.daySlots) {
				return;
			}
			this.allSlots = (fulfilmentState.daySlots as any)[
				fulfilmentState.clientSelectedDate?.toString() || fulfilmentState.selectedDate?.toString() || ''
			]?.filter((fulfillmentWindowSummarySlot: FulfilmentWindowSummarySlot) =>
				[
					FulfilmentWindowSummarySlot.StatusEnum.Available,
					FulfilmentWindowSummarySlot.StatusEnum.ClosingSoon,
					FulfilmentWindowSummarySlot.StatusEnum.Selected,
					FulfilmentWindowSummarySlot.StatusEnum.Full,
					FulfilmentWindowSummarySlot.StatusEnum.Closed,
				].includes(<FulfilmentWindowSummarySlot.StatusEnum>fulfillmentWindowSummarySlot.status)
			);
			this.maxItemQuantityTotalAllowedForFlexibleDelivery = this.allSlots?.find(
				(slot) => slot.isFlexibleDelivery
			)?.itemsLimit;
		});
		this.appSettingsService.state$.pipe(takeUntil(this.destroyed$)).subscribe((appSettingsState) => {
			this.expressFulfilmentSettings = appSettingsState.expressFulfilmentSettings;
		});
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	getSelected(slot: FulfilmentWindowSummarySlot): void {
		this._selectedSlot = slot;
		this.isOkEnabled = slot != null;
	}

	handleOk(): void {
		if (this._selectedSlot?.id === undefined) {
			this.close();
		} else {
			this.fulfilmentService.setFlexibleSelectedSlot(this._selectedSlot);
			this.close();
		}
	}

	getFlexibleDeliveryMaxItems(): number | undefined {
		return this.maxItemQuantityTotalAllowedForFlexibleDelivery;
	}

	getFlexibleFulfilmentFee(): number | undefined {
		return this.expressFulfilmentSettings?.flexibleDeliveryFee;
	}
}
